import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Phone, MessageSquare } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const faqCategories = [
  {
    name: 'General',
    questions: [
      {
        q: 'What types of loans does Close Now Lending offer?',
        a: 'We offer a full range of real estate investment loan products including Hard Money Loans, Fix & Flip Loans, Cash Out Refinance, DSCR (Debt Service Coverage Ratio) Loans, New Construction Loans, Commercial/Multi-Family Loans, Bridge Loans, and Short-Term Rental Loans.'
      },
      {
        q: 'What states do you lend in?',
        a: 'Close Now Lending provides financing nationwide across all 50 states. Our largest markets include Texas, California, New York, Florida, Illinois, and many more. Contact us to confirm availability in your specific area.'
      },
      {
        q: 'Who qualifies for your loans?',
        a: 'Our loans are designed for real estate investors, including first-time investors. We focus on the deal and the property rather than your personal credit score. While we review credit, we are asset-based lenders and can work with borrowers who may not qualify for traditional bank financing.'
      },
      {
        q: 'Do you work with first-time investors?',
        a: 'Absolutely. We welcome first-time real estate investors and are happy to guide you through the process. Our team has the experience to help you understand each step, from application to closing.'
      }
    ]
  },
  {
    name: 'Loan Process',
    questions: [
      {
        q: 'How fast can you close a loan?',
        a: 'We can close loans in as few as 10 business days. In many cases, we can provide pre-approval within 24 hours of receiving your application. Speed is one of our core advantages, especially for time-sensitive deals.'
      },
      {
        q: 'What documents do I need to apply?',
        a: 'The documents needed vary by loan type, but typically include: a completed loan application, purchase contract or property details, proof of funds for down payment, entity documents (if using an LLC), a brief description of your investment plan, and a recent credit report authorization. We keep the paperwork minimal compared to traditional banks.'
      },
      {
        q: 'How do I get started with the loan application?',
        a: 'You can get started by clicking the "Get Started" button on our website, calling us at 903-476-2937, or visiting our Contact page. A member of our team will reach out to discuss your deal and walk you through the next steps.'
      },
      {
        q: 'What is the typical loan-to-value (LTV) ratio you offer?',
        a: 'Our LTV ratios vary by loan product. For purchase loans, we typically lend up to 80-90% of the purchase price. For fix & flip projects, we can finance up to 90% of the purchase price and 100% of the rehab budget in many cases. DSCR and cash-out refinance loans are typically up to 75-80% LTV.'
      }
    ]
  },
  {
    name: 'Rates & Fees',
    questions: [
      {
        q: 'What are your interest rates?',
        a: 'Interest rates vary based on the loan product, property type, borrower experience, and market conditions. Our rates are competitive within the private lending space. Contact us for a personalized quote tailored to your specific deal.'
      },
      {
        q: 'Do you charge origination points?',
        a: 'Origination fees vary by loan product, deal structure, and borrower experience. We work to keep our pricing competitive and transparent — you\'ll receive a clear breakdown of all fees before closing so there are no surprises. Contact us for a personalized quote on your specific deal.'
      },
      {
        q: 'Are there prepayment penalties?',
        a: 'Prepayment terms vary by loan product. Short-term loans like fix and flip and bridge loans typically have no prepayment penalties. Long-term products like DSCR loans may include a prepayment penalty period, which is standard in the industry. We\'ll clearly outline all prepayment terms upfront so you can plan your exit strategy accordingly.'
      },
      {
        q: 'What fees should I expect at closing?',
        a: 'Typical closing costs include title insurance, escrow fees, appraisal fees, and recording fees. These are standard costs in any real estate transaction. We do not charge hidden or junk fees, and we will provide you with a clear breakdown before closing.'
      }
    ]
  },
  {
    name: 'Loan Products',
    questions: [
      {
        q: 'What is a DSCR loan?',
        a: 'A DSCR (Debt Service Coverage Ratio) loan is an investment property loan where qualification is based on the property\'s rental income rather than your personal income. If the property\'s rental income covers the monthly mortgage payment (typically at a 1.0x ratio or higher), you can qualify. This is ideal for investors who have strong rental properties but may not show high personal income on tax returns.'
      },
      {
        q: 'What is a Hard Money Loan?',
        a: 'A hard money loan is a short-term, asset-based loan secured by real property. Unlike traditional bank loans that focus heavily on the borrower\'s creditworthiness, hard money loans primarily consider the value of the property being used as collateral. They are popular for fix-and-flip projects, bridge financing, and situations requiring fast closings.'
      },
      {
        q: 'What is a Fix & Flip loan?',
        a: 'A fix & flip loan is short-term financing designed for investors who purchase properties, renovate them, and sell them for a profit. These loans typically cover both the purchase price and the renovation costs. Loan terms are usually 6-18 months, aligned with your project timeline.'
      },
      {
        q: 'Do you offer construction loans?',
        a: 'Yes. We offer ground-up new construction loans for residential investment properties. Funds are disbursed in draws as construction progresses, and our team works with you to ensure smooth funding throughout the build process.'
      },
      {
        q: 'What are Commercial and Multi-Family loans?',
        a: 'Commercial and multi-family loans are designed for income-producing properties with 5 or more units, mixed-use buildings, retail centers, office spaces, and other commercial real estate. These loans are structured based on the property\'s income potential, and terms can range from short-term bridge financing to longer-term stabilized loans depending on your investment strategy.'
      },
      {
        q: 'What types of commercial properties do you finance?',
        a: 'We finance a wide range of commercial and multi-family properties including apartment buildings (5+ units), mixed-use properties, retail and office space, warehouses, self-storage facilities, and other income-producing commercial real estate. Whether you\'re acquiring, refinancing, or repositioning a commercial asset, we have flexible loan structures to fit your deal.'
      },
      {
        q: 'Can I use a DSCR loan for a multi-family property?',
        a: 'Yes. DSCR loans are an excellent fit for multi-family properties. The qualification is based on the property\'s rental income relative to the debt service — not your personal income. For multi-family assets with strong occupancy and rental income, DSCR financing can provide competitive long-term rates with no income documentation required.'
      }
    ]
  }
];

const FAQPage = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', ...faqCategories.map(c => c.name)];

  const filteredFaqs = faqCategories
    .filter(cat => activeCategory === 'All' || cat.name === activeCategory)
    .map(cat => ({
      ...cat,
      questions: cat.questions.filter(
        faq =>
          faq.q.toLowerCase().includes(search.toLowerCase()) ||
          faq.a.toLowerCase().includes(search.toLowerCase())
      )
    }))
    .filter(cat => cat.questions.length > 0);

  return (
    <div className="min-h-screen">
      <SEOHead
        title="FAQ | Close Now Lending - Frequently Asked Questions About Real Estate Loans"
        description="Find answers to common questions about hard money loans, DSCR loans, fix and flip financing, loan rates, and the application process at Close Now Lending."
        keywords="hard money loan FAQ, DSCR loan questions, fix and flip loan process, real estate investment loan FAQ, Close Now Lending FAQ, private money lender questions, loan rates, loan application process"
        url="/faq"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqCategories.flatMap(cat => cat.questions.map(faq => ({
            "@type": "Question",
            "name": faq.q,
            "acceptedAnswer": { "@type": "Answer", "text": faq.a }
          })))
        })}</script>
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="bg-slate-900 pt-28 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
              Frequently Asked <span className="text-blue-400">Questions</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-xl">
              Everything you need to know about our loan programs, rates, and the application process.
            </p>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row gap-4 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                data-testid="faq-search"
                placeholder="Search questions..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 flex-wrap justify-center">
              {categories.map(cat => (
                <button
                  key={cat}
                  data-testid={`faq-category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeCategory === cat
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-16" data-testid="faq-no-results">
                <p className="text-gray-500 text-lg mb-2">No questions match your search.</p>
                <p className="text-gray-400 text-sm">Try different keywords or browse all categories.</p>
              </div>
            ) : (
              filteredFaqs.map(cat => (
                <div key={cat.name} className="mb-10" data-testid={`faq-section-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-600 inline-block">
                    {cat.name}
                  </h2>
                  <Accordion type="single" collapsible className="w-full">
                    {cat.questions.map((faq, idx) => (
                      <AccordionItem key={idx} value={`${cat.name}-${idx}`}>
                        <AccordionTrigger
                          data-testid={`faq-q-${cat.name.toLowerCase().replace(/\s+/g, '-')}-${idx}`}
                          className="text-left text-gray-900 font-semibold text-base hover:no-underline hover:text-blue-600 py-5"
                        >
                          {faq.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-gray-600 leading-relaxed text-base pb-5">
                          {faq.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4">Still Have Questions?</h2>
            <p className="text-gray-600 mb-8 text-base">
              Our team is ready to help you find the right loan for your next investment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+19034762937">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3" data-testid="faq-call-btn">
                  <Phone className="h-4 w-4 mr-2" />
                  Call 903-476-2937
                </Button>
              </a>
              <a href="/contact">
                <Button variant="outline" className="px-8 py-3 border-gray-300" data-testid="faq-contact-btn">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send a Message
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
